/**
 * DisabledAffordance — small reusable wrapper for the "rendered but disabled"
 * pattern that every Slice 0.4 read-only renderer uses.
 *
 * The "why is this disabled" copy is surfaced through `VqeTooltip`, NOT the
 * native `title=` attribute alone: the shadcn `<Button disabled>` base class
 * sets `disabled:pointer-events-none`, so a disabled button emits no pointer
 * events and a `title` tooltip can never appear on it. `VqeTooltip` wraps the
 * control in its own real-box `<span>`, which DOES receive hover — that span is
 * what makes the explanation reachable.
 *
 * `title` is still written on the button as the machine-readable mirror of the
 * same copy: it is the stable contract a large set of unit + E2E specs assert
 * against ("this control is disabled AND says why"), and it is inert visually
 * (the button never receives hover, so the browser never renders it).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.4 — Read-only Render Scaffolding.
 */
import type { ReactNode } from 'react';

import { Button } from '../../../../../components/ui/button';
import { VqeTooltip } from '../../../visual-query-editor/render/common/VqeTooltip';

const DEFAULT_TOOLTIP =
  'Editing isn’t available here — use “Edit as SQL” to make changes.';

export interface DisabledAffordanceProps {
  readonly children: ReactNode;
  /** Optional override of the default tooltip. */
  readonly tooltip?: string;
  /** Required `data-testid` so unit + E2E specs can locate the affordance. */
  readonly testId: string;
  /** Optional className for layout fine-tuning. */
  readonly className?: string;
  /** Optional accessible name — pass it when the visible label is a bare verb
   *  ("Set…", "Edit…") whose subject lives in the surrounding row label. */
  readonly ariaLabel?: string;
  /** Visual variant (matches shadcn `ButtonProps`). */
  readonly variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';
  /** Visual size (matches shadcn `ButtonProps`). */
  readonly size?: 'default' | 'sm' | 'lg' | 'icon';
}

export function DisabledAffordance({
  children,
  tooltip = DEFAULT_TOOLTIP,
  testId,
  className,
  ariaLabel,
  variant = 'ghost',
  size = 'sm',
}: DisabledAffordanceProps) {
  return (
    <VqeTooltip content={tooltip} selfProvider>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className ?? 'h-7 gap-1 px-2 text-xs text-muted-foreground'}
        disabled
        aria-disabled
        aria-label={ariaLabel}
        data-testid={testId}
        title={tooltip}
      >
        {children}
      </Button>
    </VqeTooltip>
  );
}
